import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface DctfFilterOption {
  value: string;
  label: string;
  description?: string;
}

export interface DctfFilterGroup {
  key: string;
  label: string;
  options: DctfFilterOption[];
}

interface Props {
  label: string;
  placeholder?: string;
  groups: DctfFilterGroup[];
  value: string[];
  disabled?: boolean;
  emptyText?: string;
  onChange: (value: string[]) => void;
}

export function DctfMultiSelectFilter({
  label,
  placeholder = 'Todos',
  groups,
  value,
  disabled = false,
  emptyText = 'Nenhuma opção disponível',
  onChange,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const options = groups.flatMap((group) => group.options);
  const optionValues = options.map((option) => option.value);
  const selectedValues = new Set(value);
  const allSelected =
    optionValues.length > 0 && optionValues.every((item) => selectedValues.has(item));
  const selectedCount = optionValues.filter((item) => selectedValues.has(item)).length;
  const searchTerm = search.trim().toLocaleLowerCase('pt-BR');
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      visibleOptions: group.label.toLocaleLowerCase('pt-BR').includes(searchTerm)
        ? group.options
        : group.options.filter((option) =>
            `${option.label} ${option.description ?? ''} ${option.value}`
              .toLocaleLowerCase('pt-BR')
              .includes(searchTerm),
          ),
    }))
    .filter((group) => group.visibleOptions.length > 0);

  const toggleOption = (optionValue: string) => {
    const next = new Set(selectedValues);
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onChange(optionValues.filter((item) => next.has(item)));
  };

  const toggleGroup = (group: DctfFilterGroup) => {
    const groupValues = group.options.map((option) => option.value);
    const groupIsSelected =
      groupValues.length > 0 && groupValues.every((item) => selectedValues.has(item));
    const next = new Set(selectedValues);
    groupValues.forEach((item) => (groupIsSelected ? next.delete(item) : next.add(item)));
    onChange(optionValues.filter((item) => next.has(item)));
  };

  const selectionLabel =
    selectedCount === 0 || allSelected
      ? placeholder
      : selectedCount === 1
        ? options.find((option) => selectedValues.has(option.value))?.label
        : `${selectedCount} selecionados`;

  const closeMenu = React.useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  React.useEffect(() => {
    if (!open || disabled) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !rootRef.current?.contains(event.target)) {
        closeMenu();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [disabled, open, closeMenu]);

  React.useEffect(() => {
    if (disabled) closeMenu();
  }, [disabled, closeMenu]);

  return (
    <div ref={rootRef} className="relative min-w-[15rem] flex-1">
      <label className="mb-1.5 block text-sm font-medium leading-none text-agnes-text">
        {label}
      </label>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (open) closeMenu();
          else setOpen(true);
        }}
        className="flex h-12 w-full items-center justify-between rounded-md border border-agnes-border bg-agnes-bg-surface px-3 text-left text-sm text-agnes-text disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span>{selectionLabel}</span>
        <ChevronDown aria-hidden="true" className="h-4 w-4 text-agnes-text-muted" />
      </button>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 max-h-80 w-full overflow-auto rounded-md border border-agnes-border bg-agnes-bg-surface p-2 shadow-lg">
          {options.length > 0 ? (
            <>
              <input
                type="search"
                aria-label={`Buscar ${label.toLocaleLowerCase('pt-BR')}`}
                placeholder={`Buscar ${label.toLocaleLowerCase('pt-BR')}`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="mb-2 h-9 w-full rounded-md border border-agnes-border bg-agnes-bg-surface px-2 text-sm text-agnes-text"
              />
              <button
                type="button"
                aria-pressed={allSelected}
                onClick={() => onChange(allSelected ? [] : optionValues)}
                className="mb-2 w-full rounded px-2 py-2 text-left text-sm font-semibold text-agnes-primary hover:bg-agnes-bg-subtle"
              >
                Todos
              </button>
              {visibleGroups.map((group) => {
                const groupIsSelected =
                  group.options.length > 0 &&
                  group.options.every((option) => selectedValues.has(option.value));
                return (
                  <fieldset key={group.key} className="border-t border-agnes-border py-2">
                    <legend className="sr-only">{group.label}</legend>
                    <button
                      type="button"
                      aria-pressed={groupIsSelected}
                      onClick={() => toggleGroup(group)}
                      className="w-full rounded px-2 py-1 text-left text-sm font-semibold text-agnes-text hover:bg-agnes-bg-subtle"
                    >
                      {group.label}
                    </button>
                    <div className="flex flex-col gap-1">
                      {group.visibleOptions.map((option) => (
                        <label
                          key={option.value}
                          className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-sm text-agnes-text hover:bg-agnes-bg-subtle"
                        >
                          <input
                            type="checkbox"
                            checked={selectedValues.has(option.value)}
                            onChange={() => toggleOption(option.value)}
                            className="mt-0.5 accent-agnes-primary"
                          />
                          <span>
                            <span className="block">{option.label}</span>
                            {option.description && (
                              <span className="block text-xs text-agnes-text-muted">
                                {option.description}
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                );
              })}
              {visibleGroups.length === 0 && (
                <p className="px-2 py-3 text-sm text-agnes-text-muted">
                  Nenhum resultado encontrado
                </p>
              )}
            </>
          ) : (
            <p className="px-2 py-3 text-sm text-agnes-text-muted">{emptyText}</p>
          )}
        </div>
      )}
    </div>
  );
}
